(async function () {
  'use strict';

  const PARAM = 'pin';
  const GRID_VIEW = 'grid-item';
  const LOOP_SELECTOR = '[data-collection-products] .grid';
  const CARD_SELECTOR = '.product-grid-item';
  const RUN_FLAG = '__tom_pin_injector_v20__';

  if (window[RUN_FLAG]) return;
  window[RUN_FLAG] = true;

  const qs = new URLSearchParams(location.search);
  const pinRaw = qs.get(PARAM);
  if (!pinRaw) return;

  const splitList = (str) =>
    String(str || '')
      .split(/[|,]/g)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => s.replace(/\+/g, '%20'))
      .map((s) => {
        try {
          return decodeURIComponent(s);
        } catch {
          return s;
        }
      });

  const normalizeText = (s) =>
    String(s || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');

  const waitForElement = (selector, timeout = 8000) =>
    new Promise((resolve) => {
      const ready = document.querySelector(selector);
      if (ready) return resolve(ready);

      const observer = new MutationObserver(() => {
        const el = document.querySelector(selector);
        if (el) {
          observer.disconnect();
          resolve(el);
        }
      });

      observer.observe(document.documentElement, {
        childList: true,
        subtree: true
      });

      setTimeout(() => {
        observer.disconnect();
        resolve(document.querySelector(selector));
      }, timeout);
    });

  const extractHandleFromHref = (href) => {
    if (!href) return '';
    const m = String(href).match(/\/products\/([^/?#]+)/i);
    return m ? m[1] : '';
  };

  const getHandleFromCardElement = (el) => {
    if (!el) return '';

    if (el.dataset?.pinHandle) return String(el.dataset.pinHandle).trim();

    const direct =
      el.getAttribute?.('data-product-handle') ||
      el.dataset?.productHandle ||
      el.dataset?.handle;

    if (direct) return String(direct).trim();

    const a =
      el.querySelector('a[data-grid-link]') ||
      el.querySelector('a[href*="/products/"]');

    return extractHandleFromHref(a?.getAttribute('href'));
  };

  const resolveHandleFromDomById = (loop, productId) => {
    const pid = String(productId || '').trim();
    if (!pid) return '';

    const cards = Array.from(loop.querySelectorAll(CARD_SELECTOR));
    for (const el of cards) {
      const idAttr = el.getAttribute('id') || '';
      if (idAttr.includes(`--${pid}--`)) {
        const handle = getHandleFromCardElement(el);
        if (handle) return handle;
      }
    }
    return '';
  };

  async function resolveHandleBySuggest(query, preferTitleExact, pickIndex = 1) {
    const q = String(query || '').trim();
    if (!q) return '';

    const url =
      `/search/suggest.json?q=${encodeURIComponent(q)}` +
      `&resources[type]=product` +
      `&resources[limit]=20` +
      `&resources[options][unavailable_products]=last`;

    const r = await fetch(url, { credentials: 'same-origin' });
    if (!r.ok) return '';

    const data = await r.json().catch(() => null);
    const products = data?.resources?.results?.products;
    if (!Array.isArray(products) || !products.length) return '';

    if (preferTitleExact) {
      const want = normalizeText(q);
      const exactList = products.filter((p) => normalizeText(p.title) === want);
      const exact = exactList[pickIndex - 1];
      if (exact?.handle) return exact.handle;
      return '';
    }

    const target = products[pickIndex - 1];
    return target?.handle || '';
  }

  const parsePinToken = (token) => {
    const t = String(token || '').trim();
    if (!t) return null;

    const m = t.match(/^(title|id|handle)?:?([^#]+?)(?:#(\d+))?$/i);
    if (!m) return null;

    const rawType = (m[1] || '').toLowerCase();
    const value = String(m[2] || '').trim();
    const index = Math.max(1, parseInt(m[3] || '1', 10) || 1);

    let type = rawType;
    if (!type) type = /^\d+$/.test(value) ? 'id' : 'handle';

    return { type, value, index };
  };

  async function resolveToHandle(loop, spec) {
    if (!spec) return '';

    if (spec.type === 'handle') return spec.value;

    if (spec.type === 'title') {
      return (await resolveHandleBySuggest(spec.value, true, spec.index)) || '';
    }

    if (spec.type === 'id') {
      return resolveHandleFromDomById(loop, spec.value) || '';
    }

    return '';
  }

  async function fetchGridCardHtml(handle) {
    const url = `/products/${encodeURIComponent(handle)}?view=${encodeURIComponent(GRID_VIEW)}`;
    const r = await fetch(url, { credentials: 'same-origin' });
    if (!r.ok) throw new Error(`grid-item HTTP ${r.status}`);
    return await r.text();
  }

  const pickCardFromHtml = (html) => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.querySelector(CARD_SELECTOR);
  };

  const insertAfterNode = (parent, node, after) => {
    parent.insertBefore(node, after.nextSibling);
  };

  const findExistingCardByHandle = (loop, handle) => {
    const cards = Array.from(loop.querySelectorAll(CARD_SELECTOR));
    for (const el of cards) {
      const h = getHandleFromCardElement(el);
      if (h === handle && !el.classList.contains('pinned-product')) {
        return el;
      }
    }
    return null;
  };

  const revealElement = (el) => {
    if (!el || el.nodeType !== 1) return;

    el.hidden = false;
    el.removeAttribute('hidden');
    el.removeAttribute('aria-hidden');
    el.removeAttribute('data-aos');
    el.removeAttribute('data-aos-anchor');
    el.removeAttribute('data-aos-delay');
    el.removeAttribute('data-aos-duration');
    el.removeAttribute('data-aos-offset');
    el.removeAttribute('data-scroll-trigger');
    el.removeAttribute('inert');

    el.classList.remove(
      'aos-init',
      'aos-animate',
      'lazyload',
      'lazyloading',
      'is-hidden',
      'hidden',
      'invisible'
    );

    const style = el.style;
    if (style) {
      if (style.opacity === '0') style.opacity = '1';
      if (style.visibility === 'hidden') style.visibility = 'visible';
      if (style.transform) style.transform = 'none';
      if (style.pointerEvents === 'none') style.pointerEvents = '';
      if (style.display === 'none') style.display = '';
    }
  };

  const hydrateMedia = (card) => {
    if (!card) return;

    const sources = card.querySelectorAll('source');
    sources.forEach((source) => {
      const srcset =
        source.getAttribute('data-srcset') ||
        source.dataset?.srcset;
      if (srcset && !source.getAttribute('srcset')) {
        source.setAttribute('srcset', srcset);
      }
    });

    const imgs = card.querySelectorAll('img');
    imgs.forEach((img) => {
      const src =
        img.getAttribute('data-src') ||
        img.dataset?.src;

      const srcset =
        img.getAttribute('data-srcset') ||
        img.dataset?.srcset;

      if (src && (!img.getAttribute('src') || img.getAttribute('src') === 'data:,')) {
        img.setAttribute('src', src);
      }

      if (srcset && !img.getAttribute('srcset')) {
        img.setAttribute('srcset', srcset);
      }

      img.removeAttribute('loading');
      img.removeAttribute('decoding');
      img.classList.remove('lazyload', 'lazyloading');
      img.style.opacity = '1';
      img.style.visibility = 'visible';
    });
  };

  const normalizeFetchedCard = (card) => {
    if (!card) return;

    // 只处理卡片根节点
    revealElement(card);

    // 只处理图片相关节点，不碰颜色展开/折叠节点
    const mediaNodes = card.querySelectorAll('picture, img, source');
    mediaNodes.forEach((el) => revealElement(el));

    hydrateMedia(card);

    card.style.opacity = '1';
    card.style.visibility = 'visible';
    card.style.transform = 'none';
    card.style.display = '';
  };

  const refreshLayout = (card) => {
    if (!card) return;

    card.offsetHeight;

    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
      document.dispatchEvent(new Event('theme:resize'));
      document.dispatchEvent(new Event('shopify:section:load'));
      document.dispatchEvent(new CustomEvent('tom:pin-card-mounted', { detail: { card } }));
    });
  };

  const removeDuplicates = (loop, insertedHandles) => {
    if (!insertedHandles.size) return;

    const seenPinned = new Set();
    const cards = Array.from(loop.querySelectorAll(CARD_SELECTOR));

    for (const el of cards) {
      const handle = getHandleFromCardElement(el);
      if (!handle || !insertedHandles.has(handle)) continue;

      if (el.classList.contains('pinned-product')) {
        if (seenPinned.has(handle)) {
          el.remove();
          // console.log('🧹 Removed extra pinned duplicate:', handle);
        } else {
          seenPinned.add(handle);
        }
        continue;
      }

      el.remove();
      // console.log('🧹 Removed original duplicate:', handle);
    }
  };

  const trimToOriginalCount = (loop, originalCount) => {
    let cards = Array.from(loop.querySelectorAll(CARD_SELECTOR));
    if (cards.length <= originalCount) return;

    for (let i = cards.length - 1; i >= 0 && cards.length > originalCount; i--) {
      const el = cards[i];
      if (el.classList.contains('pinned-product')) continue;
      el.remove();
      cards = Array.from(loop.querySelectorAll(CARD_SELECTOR));
      // console.log('✂️ Trimmed trailing card to keep page size stable');
    }
  };

  const loop = await waitForElement(LOOP_SELECTOR, 8000);
  if (!loop) {
    console.warn('⚠️ Pin injector: grid not found');
    return;
  }

  const originalCount = loop.querySelectorAll(CARD_SELECTOR).length;
  const tokens = splitList(pinRaw).map(parsePinToken).filter(Boolean);
  if (!tokens.length) return;



  const marker = document.createComment('tom-pin-marker');
  loop.insertBefore(marker, loop.firstChild);

  let cursor = marker;
  const insertedHandles = new Set();

  for (const spec of tokens) {
    try {
      const handle = await resolveToHandle(loop, spec);
      if (!handle) {
        console.warn('⚠️ Could not resolve handle:', spec);
        continue;
      }

      if (insertedHandles.has(handle)) continue;

      let card = findExistingCardByHandle(loop, handle);
      let fetched = false;

      if (card) {
        // console.log('↪ Reusing existing card:', handle);
      } else {
        // console.log('↪ Fetching new card:', handle);
        const html = await fetchGridCardHtml(handle);
        card = pickCardFromHtml(html);
        fetched = true;
      }

      if (!card) {
        // console.warn('⚠️ No card found for:', handle);
        continue;
      }

      if (fetched) {
        normalizeFetchedCard(card);
      }

      card.classList.add('pinned-product');
      card.dataset.pinHandle = handle;

      insertAfterNode(loop, card, cursor);
      cursor = card;
      insertedHandles.add(handle);

      normalizeFetchedCard(card);
      refreshLayout(card);

      // console.log('✅ Inserted pinned:', handle);
    } catch (err) {
      console.error('❌ Insert failed:', spec, err);
    }
  }

  removeDuplicates(loop, insertedHandles);
  trimToOriginalCount(loop, originalCount);

  const mo = new MutationObserver(() => {
    removeDuplicates(loop, insertedHandles);
    trimToOriginalCount(loop, originalCount);
  });

  mo.observe(loop, {
    childList: true,
    subtree: false
  });

  if (insertedHandles.size) {
    // console.log('🎯 Total pinned inserted:', insertedHandles.size, Array.from(insertedHandles));
  } else {
    // console.warn('⚠️ No pinned products inserted');
  }

  console.groupEnd();
})();